import { FC } from 'react';

import {
  DELIVERY_CHANNEL_CONFIG,
  DeliveryChannel,
  GiftCoupon,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import { formatPhone } from '../../utils/couponSearch.utils';
import { formatDate } from './utils';

interface RenewalCouponModalProps {
  coupon: GiftCoupon;
  onClose: () => void;
}

export const RenewalCouponModal: FC<RenewalCouponModalProps> = ({ coupon, onClose }) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800">연장쿠폰 정보</h3>
            <p className="text-xs text-gray-500">발급된 연장쿠폰의 상세 정보</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-5">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                  쿠폰 ID
                </td>
                <td className="py-2 font-mono text-xs text-gray-700">{coupon._id}</td>
              </tr>
              <tr>
                <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                  쿠폰코드
                </td>
                <td className="py-2 text-gray-700">
                  #{coupon.seq} / {coupon.code}
                </td>
              </tr>
              <tr>
                <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                  상태
                </td>
                <td className="py-2 text-gray-700">{coupon.redeemStatus}</td>
              </tr>
              <tr>
                <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                  공유상태
                </td>
                <td className="py-2 text-gray-700">{coupon.shareStatus}</td>
              </tr>
              {coupon.selectedChoiceUnit &&
                typeof coupon.selectedChoiceUnit === 'object' && (
                  <tr>
                    <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                      상품
                    </td>
                    <td className="py-2 text-gray-700">
                      {(coupon.selectedChoiceUnit as { name?: string }).name || '-'}
                    </td>
                  </tr>
                )}
              <tr>
                <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                  구매일
                </td>
                <td className="py-2 text-gray-700">{formatDate(coupon.purchasedAt)}</td>
              </tr>
              <tr>
                <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                  만료일
                </td>
                <td className="py-2 text-gray-700">{formatDate(coupon.expiresAt)}</td>
              </tr>
              {coupon.recipient && (
                <>
                  <tr>
                    <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                      교환 신청자
                    </td>
                    <td className="py-2 text-gray-700">{coupon.recipient.name}</td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                      신청자 연락처
                    </td>
                    <td className="py-2 text-gray-700">
                      {formatPhone(coupon.recipient.phoneNumber)}
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                      교환 신청일
                    </td>
                    <td className="py-2 text-gray-700">
                      {formatDate(coupon.recipient.requestedAt)}
                    </td>
                  </tr>
                </>
              )}
              {coupon.gifticon && (
                <>
                  <tr>
                    <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                      발송일
                    </td>
                    <td className="py-2 text-gray-700">
                      {formatDate(coupon.gifticon.deliveredAt)}
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                      발송업체
                    </td>
                    <td className="py-2 text-gray-700">
                      {DELIVERY_CHANNEL_CONFIG[
                        coupon.gifticon.deliveryChannel as DeliveryChannel
                      ]?.label || coupon.gifticon.deliveryChannel}
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                      도매가
                    </td>
                    <td className="py-2 text-gray-700">
                      {coupon.gifticon.priceWhole?.toLocaleString()}원
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                      판매가
                    </td>
                    <td className="py-2 text-gray-700">
                      {coupon.gifticon.priceRetail?.toLocaleString()}원
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                      리베이트
                    </td>
                    <td className="py-2 text-gray-700">
                      {coupon.gifticon.priceRebate?.toLocaleString()}원
                    </td>
                  </tr>
                </>
              )}
              {coupon.sharedAt && (
                <tr>
                  <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                    공유일
                  </td>
                  <td className="py-2 text-gray-700">{formatDate(coupon.sharedAt)}</td>
                </tr>
              )}
              {coupon.viewedAt && (
                <tr>
                  <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                    열람일
                  </td>
                  <td className="py-2 text-gray-700">{formatDate(coupon.viewedAt)}</td>
                </tr>
              )}
              {coupon.confirmedAt && (
                <tr>
                  <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                    확인일
                  </td>
                  <td className="py-2 text-gray-700">{formatDate(coupon.confirmedAt)}</td>
                </tr>
              )}
              <tr>
                <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-500">
                  생성일
                </td>
                <td className="py-2 text-gray-700">{formatDate(coupon.createdAt)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="border-t px-5 py-3">
          <button
            onClick={onClose}
            className="w-full rounded-lg bg-gray-800 py-2.5 text-sm font-medium text-white transition hover:bg-gray-900"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};
